import { z } from 'zod';

const directory_user = z
  .object({
    id: z.number().int(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    email: z.string().nullish(),
    phone_number: z.string().nullish(),
  })
  .passthrough();
const directory_admin = z
  .object({ id: z.number().int(), user: directory_user.nullish() })
  .passthrough();
const directory_contact = z
  .object({
    id: z.number().int(),
    avatar_url: z.string().nullish(),
    user: directory_user.nullish(),
  })
  .passthrough();
const directory_account = z
  .object({
    id: z.number().int(),
    name: z.string().nullish(),
    active: z.boolean(),
    rate_type: z.enum(['STATIC', 'MARKUP']),
    markup_percent: z.number(),
    default_contact: directory_contact.nullish(),
    contacts: z.array(directory_contact),
  })
  .passthrough();
const directory_rate = z
  .object({
    id: z.number().int(),
    pay: z.number().int(),
    cost: z.number().int(),
    account_id: z.number().int().nullish(),
    address_id: z.number().int(),
    skill: z
      .object({ id: z.number().int(), name: z.string().nullable() })
      .partial()
      .passthrough()
      .nullish(),
  })
  .passthrough();
const directory_address = z
  .object({
    id: z.number().int(),
    active: z.boolean(),
    name: z.string().nullish(),
    line1: z.string().nullish(),
    line2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zip: z.string().nullish(),
    timezone: z.string().nullish(),
    coords: z
      .object({
        latitude: z.number().nullable(),
        longitude: z.number().nullable(),
      })
      .partial()
      .passthrough(),
    rates: z.array(directory_rate),
  })
  .passthrough();
const directory_customer = z
  .object({
    id: z.number().int(),
    name: z.string().nullable(),
    logo_url: z.string().nullish(),
    status: z.number().int().nullish(),
    job_tax_type: z.string().nullish(),
    lta_allowed: z.boolean().nullish(),
    require_background_checks: z.boolean().optional(),
    enable_surge_rate: z.boolean().optional(),
    admins: z.array(directory_admin),
    accounts: z.array(directory_account),
    addresses: z.array(directory_address),
  })
  .passthrough();
const customers_directory_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        actor: z
          .object({
            role: z.string(),
            scoped_customer_ids: z.array(z.number().int()),
          })
          .passthrough(),
        customers: z.array(directory_customer),
      })
      .passthrough(),
  })
  .passthrough();
const error_response = z
  .object({ success: z.literal(false), errors: z.array(z.string()) })
  .passthrough();
const rate_quote_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({ pay: z.number().int(), cost: z.number().int() })
      .passthrough(),
  })
  .passthrough();
const rate_range_response = z
  .object({
    success: z.literal(true),
    data: z.object({ minimum: z.number(), maximum: z.number() }).passthrough(),
  })
  .passthrough();
const order_template_summary = z
  .object({
    id: z.number().int(),
    order_type: z.string(),
    primary_skill: z.string().nullish(),
    skills: z.array(z.string()),
    location_label: z.string().nullish(),
    jobs_count: z.number().int(),
    used_count: z.number().int(),
    last_used_at: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough();
const order_templates_response = z
  .object({ success: z.literal(true), data: z.array(order_template_summary) })
  .passthrough();
const order_template_detail_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        template_id: z.number().int(),
        billing_account_id: z.number().int(),
        order_type: z.string(),
        verification_enabled: z.boolean().nullish(),
        jobs: z.array(z.object({}).partial().passthrough()),
      })
      .passthrough(),
  })
  .passthrough();
const saved_snippet = z
  .object({
    id: z.number().int(),
    account_id: z.number().int(),
    field_kind: z.string(),
    title: z.string(),
    body: z.string(),
    used_count: z.number().int(),
    last_used_at: z.string().datetime({ offset: true }).nullish(),
    created_at: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough();
const saved_snippets_list_response = z
  .object({ success: z.literal(true), data: z.array(saved_snippet) })
  .passthrough();
const postApiadminorders_v2billing_accountsBilling_account_idsaved_snippets_Body =
  z
    .object({ field_kind: z.string(), title: z.string(), body: z.string() })
    .passthrough();
const saved_snippet_detail_response = z
  .object({ success: z.literal(true), data: saved_snippet })
  .passthrough();
const shift_supervisor = z
  .object({
    id: z.number().int(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    full_name: z.string(),
    phone: z.string().nullish(),
    email: z.string().nullish(),
    external: z.boolean(),
    active: z.boolean(),
  })
  .passthrough();
const shift_supervisors_response = z
  .object({ success: z.literal(true), data: z.array(shift_supervisor) })
  .passthrough();
const postApiadminorders_v2billing_accountsBilling_account_idshift_supervisors_Body =
  z.object({ name: z.string(), contact: z.string().optional() }).passthrough();
const shift_supervisor_detail_response = z
  .object({ success: z.literal(true), data: shift_supervisor })
  .passthrough();
const location = z
  .object({
    id: z.number().int(),
    name: z.string().nullish(),
    address_line1: z.string().nullish(),
    address_line2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zip: z.string().nullish(),
    neighborhood: z.string().nullish(),
    full_address: z.string().nullish(),
    timezone: z.string().nullish(),
    active: z.boolean().nullish(),
    jobs_count: z.number().int(),
  })
  .passthrough();
const locations_response = z
  .object({ success: z.literal(true), data: z.array(location) })
  .passthrough();
const postApiadminorders_v2billing_accountsBilling_account_idlocations_Body = z
  .object({
    name: z.string(),
    address_line1: z.string(),
    address_line2: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    neighborhood: z.string(),
    coords: z.object({}).partial().passthrough(),
  })
  .partial()
  .passthrough();
const location_detail_response = z
  .object({ success: z.literal(true), data: location })
  .passthrough();
const skill = z
  .object({
    id: z.number().int(),
    name: z.string(),
    display_name_en: z.string().nullish(),
    display_name_es: z.string().nullish(),
    description_en: z.string().nullish(),
    description_es: z.string().nullish(),
    gravy_trained: z.boolean().nullish(),
    requestable: z.boolean().nullish(),
    popular_pick: z.boolean().nullish(),
    default_uniform_id: z.number().int().nullish(),
    image_url: z.string().nullish(),
    category: z
      .object({ id: z.number().int(), name: z.string() })
      .passthrough()
      .nullish(),
  })
  .passthrough();
const skills_response = z
  .object({ success: z.literal(true), data: z.array(skill) })
  .passthrough();
const skill_category = z
  .object({
    id: z.number().int(),
    name: z.string(),
    display_name_en: z.string().nullish(),
    display_name_es: z.string().nullish(),
    image_url: z.string().nullish(),
  })
  .passthrough();
const skill_categories_response = z
  .object({ success: z.literal(true), data: z.array(skill_category) })
  .passthrough();
const uniform = z
  .object({
    id: z.number().int(),
    name: z.string(),
    name_es: z.string().nullish(),
    image_url: z.string().nullish(),
    default_instructions: z.string().nullish(),
    default_instructions_es: z.string().nullish(),
  })
  .passthrough();
const uniforms_response = z
  .object({ success: z.literal(true), data: z.array(uniform) })
  .passthrough();
const certificate = z
  .object({
    id: z.number().int(),
    name: z.string(),
    image_url: z.string().nullish(),
    certificate_type: z.enum(['BADGE', 'CERTIFICATE']),
  })
  .passthrough();
const certificates_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        available: z.array(certificate),
        customer_defaults: z.array(certificate),
      })
      .passthrough(),
  })
  .passthrough();
const surge_rate = z
  .object({
    id: z.number().int(),
    date: z.string(),
    rate: z.number(),
    description: z.string().nullish(),
  })
  .passthrough();
const surge_rates_response = z
  .object({ success: z.literal(true), data: z.array(surge_rate) })
  .passthrough();
const payment_method_card = z
  .object({
    id: z.string().nullable(),
    brand: z.string().nullable(),
    last4: z.string().nullable(),
    exp_month: z.number().int().nullable(),
    exp_year: z.number().int().nullable(),
    funding: z.string().nullable(),
    country: z.string().nullable(),
    holder_name: z.string().nullable(),
  })
  .partial()
  .passthrough();
const payment_method_bank = z
  .object({
    id: z.string().nullable(),
    bank_name: z.string().nullable(),
    last4: z.string().nullable(),
    account_holder_name: z.string().nullable(),
  })
  .partial()
  .passthrough();
const payment_methods_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        cards: z.array(payment_method_card),
        bank_accounts: z.array(payment_method_bank),
        current_payment_method: z.string().nullish(),
        primary_card_label: z.string().nullish(),
        account_has_orders: z.boolean(),
      })
      .passthrough(),
  })
  .passthrough();
const workers_search_response = z
  .object({
    success: z.literal(true),
    data: z.array(z.object({}).partial().passthrough()),
  })
  .passthrough();
const workers_counts_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        all: z.number().int().nullable(),
        favourites: z.number().int().nullable(),
        highly_rated: z.number().int().nullable(),
        past_workers: z.number().int().nullable(),
        others: z.number().int().nullable(),
        trained: z.number().int().nullable(),
      })
      .partial()
      .passthrough(),
  })
  .passthrough();
const postApiadminorders_v2orders_Body = z
  .object({
    account_id: z.number().int(),
    order_type: z.string(),
    verification_enabled: z.boolean().optional(),
    jobs: z.array(z.object({}).partial().passthrough()),
  })
  .passthrough();
const order_summary = z
  .object({
    id: z.number().int().nullable(),
    uuid: z.string().nullable(),
    status: z.string().nullable(),
    start_at: z.string().datetime({ offset: true }).nullable(),
    end_at: z.string().datetime({ offset: true }).nullable(),
    jobs_count: z.number().int().nullable(),
  })
  .partial()
  .passthrough();
const order_create_response = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        order: order_summary,
        jobs_count: z.number().int(),
        workers_invited: z.number().int(),
        workers_hired: z.number().int(),
        template_id: z.number().int().nullish(),
        template_used_count: z.number().int().nullish(),
      })
      .passthrough(),
    partial_success: z.literal(true).optional(),
    errors: z.array(z.string()).optional(),
  })
  .passthrough();

export const schemas = {
  directory_user,
  directory_admin,
  directory_contact,
  directory_account,
  directory_rate,
  directory_address,
  directory_customer,
  customers_directory_response,
  error_response,
  rate_quote_response,
  rate_range_response,
  order_template_summary,
  order_templates_response,
  order_template_detail_response,
  saved_snippet,
  saved_snippets_list_response,
  postApiadminorders_v2billing_accountsBilling_account_idsaved_snippets_Body,
  saved_snippet_detail_response,
  shift_supervisor,
  shift_supervisors_response,
  postApiadminorders_v2billing_accountsBilling_account_idshift_supervisors_Body,
  shift_supervisor_detail_response,
  location,
  locations_response,
  postApiadminorders_v2billing_accountsBilling_account_idlocations_Body,
  location_detail_response,
  skill,
  skills_response,
  skill_category,
  skill_categories_response,
  uniform,
  uniforms_response,
  certificate,
  certificates_response,
  surge_rate,
  surge_rates_response,
  payment_method_card,
  payment_method_bank,
  payment_methods_response,
  workers_search_response,
  workers_counts_response,
  postApiadminorders_v2orders_Body,
  order_summary,
  order_create_response,
};
